import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'
import { DAIMI_EN_SLUG } from '@/content/daimiEnergy'

/**
 * The Future 2030 Energy & Utilities outlook page — its copy and its figures, apart
 * from the component that draws them.
 *
 * **The tenth Future 2030 outlook with a designed page of its own**, and the
 * nineteenth report overall. Asked for on 2026-08-28, in the same words as the
 * Automotive, Manufacturing, Consumer Goods, Retail, Pharmaceutical, Banking,
 * Services, Insurance and Logistics ones — *"referans alarak ilgili sayfayı birebir
 * tıpa tıp aynısını tasarla"* — as a four-part full-page capture of digitopia.co's
 * Energy & Utilities outlook page, plus the six images that page carries: the
 * wind-turbine-on-a-circuit-board cover art, the DMI forecast chart, the three
 * imperative photographs and the two-copy report mockup. And, as on the seven
 * requests before it, the instruction that the maturity band point at the 2026 DAIMI
 * report rather than the 2024 one the live page offers.
 *
 * So the bands are the reference's, in the reference's order: hero → the forecast
 * beside Figure 1 → a dark band of three imperatives → the five predictions → the
 * pointer at the maturity report → the close. The two shared closing bands
 * (`content/reportClosing.ts`) still sit where they sit on every other report page.
 *
 * **Three things here are this page's own, and all three are the reference's doing.**
 *
 *  * **The hero has no bold claim over it.** Eight of the nine sibling heroes open on
 *    a `lede` set bold — "Logistics Is Being Rewritten—By AI, Automation, and
 *    Expectation" — and this one does not: the live page runs straight into one plain
 *    paragraph. So there is no `lede` field here at all, rather than a `lede` invented
 *    to match the siblings.
 *  * **The forecast band has an outro.** "The choice is clear: transform or fall
 *    behind." sits between the three trajectories and the Download button, the way the
 *    Retail and Insurance bands do and the Logistics one does not.
 *  * **The predictions band has no lead-in.** The live page goes from the heading
 *    straight into the five, so `intro` is absent — the Insurance shape rather than
 *    the Logistics one.
 *
 * **The three trajectories were re-cast, and that is the one wording liberty here.**
 * The live band runs them as three sentences — "**Leaders** will accelerate from a DMI
 * score of 3.40 (2024)…" — with the subject bolded inside the prose. Every designed
 * report page on this site sets that label on its own line instead, which leaves the
 * rest of the sentence needing to start like one; so "will accelerate" becomes
 * "Accelerating". The claims and every figure are unchanged and
 * `OUTLOOK_EN_FIGURE.check` says so.
 *
 * **There is a sector slip in this reference, and it is in the closing band.** The
 * live page's close reads "Get the full Future 2030 **Insurance** Outlook with
 * maturity forecasts…" under a heading about the energy sector's 2030 advantage —
 * the same kind of template left half-filled that put a "Retail" maturity CTA on the
 * Pharmaceutical and Banking pages. It says Energy & Utilities here, and
 * `OUTLOOK_EN_CLOSE.check` records the correction rather than making it silently.
 *
 * **Two deliberate departures from the capture**, both the settled house call rather
 * than a reading of the reference:
 *
 *  * the h1 is the series headline every outlook page here uses ("Digital
 *    Transformation Outlook for Energy & Utilities") rather than the live page's
 *    "Future 2030: Digital Transformation Outlook for Energy & Utilities" — the
 *    eyebrow above it already says Future 2030, and the ten outlook pages read as one
 *    series this way.
 *  * the maturity band carries the 2026 Energy Industry Focus cover, which the live
 *    band does not. `OUTLOOK_EN_MATURITY.check` is where that is asked about, because
 *    it is the one place this page shows something the reference has not.
 *
 * **Where the words come from.** Everything here is transcribed from the four
 * captures, which are full-page renders read down to fit — the *shape* of the page is
 * certain and the *wording* is a careful reading of body copy set a handful of pixels
 * tall. Every band that carries prose therefore carries a `check`, the same way
 * `content/outlookLogistics.ts` does, and `outlookEnergyChecks()` feeds them into the
 * same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its
 * closing band open an embedded HubSpot form — first and last name, business email,
 * company, job title, phone, country, industry, a marketing opt-in and the privacy
 * statement. These documents are prerendered with no client bundle at all
 * (`src/prerender.tsx`), so a form script would never run, and the other thirty
 * reports on this site hand the reader the PDF from our own bucket instead. Every
 * Download button here is that PDF, and `OUTLOOK_EN_CLOSE.check` is what keeps the
 * gating question open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The short slug digitopia.co uses, and the one the request named. It was already in
 * `REPORTS` before this page existed, so the page is built under it as it stands
 * rather than moved to a `…-outlook-2030` one.
 */
export const OUTLOOK_EN_SLUG = 'energy-industry-outlook'

/**
 * The Energy Industry Focus cut of the Digital & AI Maturity Report 2026 — where this
 * page's maturity band sends the reader.
 *
 * It used to be a second copy of the literal, written out because that report had no
 * content module of its own to export a slug from, under a note saying the two had to
 * stay in step. It got a designed page on 2026-08-28, so there is one copy now and
 * `content/daimiEnergy.ts` holds it.
 *
 * **This site publishes an Energy cut and a Utilities cut as two separate reports**,
 * where the outlook covers both sectors as one. The Energy one is the closer match to
 * a page headed "Energy & Utilities" and it is what this band points at;
 * `OUTLOOK_EN_MATURITY.check` is where the choice is put to a reader.
 */
export { DAIMI_EN_SLUG }

/**
 * The hero.
 *
 * **One plain paragraph and no bold claim over it**, which is the live page's own
 * shape and the thing that separates this hero from its nine siblings'. `body` is
 * still an array so the component can grow a second paragraph without a type change,
 * and so `reports.ts` can take the meta description from `body[0]`.
 *
 * The button says "Download the Report" rather than the "Download Report" every other
 * band uses, which is also the live page's wording.
 */
export const OUTLOOK_EN_INTRO = {
  body: [
    'The energy industry is at a turning point. Technological innovation, environmental urgency, and shifting customer expectations are reshaping the sector. Our latest Future 2030 Outlook reveals how digital maturity will define winners and laggards, and what leaders must do today to secure tomorrow.',
  ],
  downloadLabel: 'Download the Report',
  /**
   * The cover art supplied with this request — the wind turbine standing on a glowing
   * circuit board, which is the artwork behind the report cover with none of its
   * lettering on it. This is where the live hero puts it too, at the same size and on
   * the same side.
   */
  photo: {
    src: '/photo/future-2030-energy-digital-transformation.webp',
    alt: 'A white wind turbine standing on a glowing blue circuit board',
  } satisfies Photo,
  check:
    'The hero paragraph is transcribed from the reference capture rather than supplied as text, so it wants reading back against digitopia.co. Note that this hero carries no bold opening claim — the nine sibling outlook pages all do, and this one does not, because the live page does not either. The image, its side and its size are the live page’s.',
}

/**
 * The forecast band: Figure 1 on the left, the three trajectories on the right.
 *
 * The three are one block on the live page in which each line opens with a bolded
 * label — "Leaders", "Average performers", "Laggards". Split into `label` and `body`
 * here so the label can be set rather than emphasised inside a sentence, which is the
 * call every designed report page on this site has made. Their openings are re-cast as
 * a result: "Leaders **will accelerate** from a DMI score of 3.40" becomes a line that
 * can start on its own.
 *
 * `outro` is present, unlike the Logistics band's: the live page closes the three with
 * a one-line verdict before the button.
 */
export const OUTLOOK_EN_FIGURE = {
  heading: 'Energy & Utilities: Digital Maturity Forecast to 2030',
  intro: [
    'By 2030, energy will no longer be defined by traditional supply and demand. Instead, AI-driven insights, renewable portfolios, intelligent grids, and consumer empowerment will set the pace.',
    'Digitopia’s analysis shows:',
  ],
  points: [
    {
      label: 'Leaders',
      body: 'Accelerating from a DMI score of 3.40 (2024) to as high as 4.22 (2029) under the optimistic scenario, dominating with innovation, agility, and customer-centricity.',
    },
    {
      label: 'Average performers',
      body: 'Improving modestly to around 3.29, maintaining stability but failing to differentiate.',
    },
    {
      label: 'Laggards',
      body: 'If they don’t act decisively, risk stagnating below 2.50, facing existential threats from rising costs, poor customer experience, and competitive disruption.',
    },
  ],
  outro: 'The choice is clear: transform or fall behind.',
  /**
   * Figure 1, the file supplied with this request: five years of actuals (2020–24) and
   * five of forecast (2025–29), the leader, average and laggard trajectories each
   * fanning out into an optimistic, moderate and pessimistic band. It is 1200×784 on a
   * white ground, so it gets a white card of its own and scales to it whole — ten year
   * columns with three labelled values each do get small on a phone, but a figure the
   * reader has to drag sideways reads as broken.
   */
  photo: {
    src: '/photo/future-2030-energy-dmi-forecast.webp',
    alt: 'Energy & Utilities DMI evolution 2020 to 2029: actual leader, average and laggard scores to 2024, then optimistic, moderate and pessimistic forecast bands to 2029',
  } satisfies Photo,
  caption: [
    'Figure 1: Energy & Utilities Industry DMI Evolution (2020–2029) with Scenario-Based Forecasts',
    'Leader, Average, and Laggard Trajectories under Optimistic, Moderate, and Pessimistic Projections',
  ],
  check:
    'All four figures in this band reconcile against Figure 1: the leader’s 3.40 is its 2024 actual, 4.22 its 2029 optimistic value, the average performers’ 3.29 the 2029 optimistic average, and the laggards’ “below 2.50” covers a 2029 band running 2.46 to 2.68. So what is left is wording, and two things want reading back. The three lines were re-cast so each could stand on its own: the live page runs "Leaders will accelerate from a DMI score of 3.40…" as prose with the label bolded inside it, and this page sets the label as a heading and opens the line with "Accelerating…". The claim and every number are unchanged; say if the running-prose form is wanted instead. And the same naming question the Banking, Services, Insurance and Logistics pages raised: this page calls the index DMI while every other page on this site calls it DAIMI, the Digital & AI Maturity Index. The chart itself is lettered DMI, so both were left as they are.',
}

/**
 * The dark imperatives band: two headline lines, then three cards.
 *
 * Prose cards, one sentence each — the shape every outlook page but Insurance has, and
 * the reference's own. The live band's second line is a title rather than a claim
 * ("Three Strategic Imperatives for 2030-Ready Energy & Utilities Industry"), so it is
 * set lighter and larger under the headline, which is what the component already does
 * with it.
 */
export const OUTLOOK_EN_REQUIREMENTS = {
  heading: 'Digital Revolution Has Arrived',
  subheading: 'Three Strategic Imperatives for 2030-Ready Energy & Utilities Industry',
  /** The eyebrow above each card's title, numbered in the order the live band has. */
  eyebrow: 'How to Get There',
  items: [
    {
      title: 'Commit to Operational Excellence',
      body: 'Embed predictive analytics, intelligent grids, and continuous improvement to optimize performance and resilience.',
      linkLabel: 'Digital Solutions',
      linkHref: LINKS.solutions,
      photo: {
        src: '/photo/energy-operational-excellence.webp',
        alt: 'Transmission pylons at dusk, the power lines drawn as streaks of coloured light against a moonlit sky',
      } satisfies Photo,
    },
    {
      title: 'Embed Analytics & AI Across the Business',
      body: 'Use AI-driven insights in asset management, customer engagement, and sustainability initiatives to unlock efficiency and growth.',
      linkLabel: 'Artificial Intelligence',
      linkHref: LINKS.enterpriseAi,
      photo: {
        src: '/photo/energy-analytics-and-ai.webp',
        alt: 'A solar array in the foreground with wind turbines and a power station cooling tower behind it at sunset',
      } satisfies Photo,
    },
    {
      title: 'Integrate Systems, Data, and Processes',
      body: 'Unify technology, data flows, and operations to enable agility, real-time decision-making, and empower employees with better tools and collaboration.',
      linkLabel: 'Digital Upskilling',
      linkHref: LINKS.transformation,
      photo: {
        src: '/photo/energy-integrated-systems.webp',
        alt: 'An engineer in a hard hat and high-visibility vest writing on a tablet in front of a wind farm',
      } satisfies Photo,
    },
  ],
  check:
    'Three things. The two headline lines and the three card sentences are transcribed from the reference capture. The reference renders that band with the three photographs missing, so the three you supplied are matched to the cards by subject — the transmission grid to operational excellence, the mixed renewable generation to analytics and AI, the engineer with a tablet to integrating systems and equipping people. Say if the live page pairs them differently. And the third card’s link is the one destination that had to change: the live page sends “Digital Upskilling” to a solution page digitopia.co has and this site does not — it came across in the import as a rebuild source rather than as a page, so linking to it would be a dead link. It points at Transformation Management until that page is built. The other two are the live destinations. This is the same substitution the nine earlier outlook pages make, and one answer settles all ten.',
}

/**
 * The predictions band, with the report mockup beside it.
 *
 * **No lead-in**, unlike the Retail, Banking and Logistics bands and like the
 * Insurance one: the live band goes from the heading straight into the five. The five
 * themselves are the clipped statements the live page prints — the last one carries no
 * full stop there and carries none here.
 */
export const OUTLOOK_EN_PREDICTIONS = {
  heading: 'Five Predictions for the Energy Industry in 2030',
  items: [
    'Smart grids become the global standard, built on connected IoT infrastructure.',
    'Energy-as-a-Service (EaaS) dominates, demanding agile, personalized solutions.',
    'Diversified portfolios with renewables and hydrogen reshape the energy mix.',
    'Prosumers rise, requiring seamless, transparent, real-time customer experiences.',
    'Cybersecurity becomes survival-critical, with AI-enhanced protections separating leaders from vulnerable players',
  ],
  /** The mockup supplied with this request — the Future 2030 cover, not a maturity cut's. */
  photo: {
    src: '/photo/future-2030-outlook-energy-mockup.webp',
    alt: 'Two copies of Future 2030: Digital Transformation Outlook for Energy',
  } satisfies Photo,
  check:
    'The heading and all five predictions are transcribed from the reference capture. Two notes. The fifth ends without a full stop, which is how the live page prints it — say if that is a typo to fix rather than a quirk to keep. And the mockup beside them is the file you supplied; the reference capture shows that half of the band empty, so say if the live page really carries no image there and it should come out. This is the same call the Retail, Banking, Pharmaceutical, Services, Insurance and Logistics pages made.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band whose content is deliberately not the live page's, and the only
 * change in this request that was asked for in words rather than shown in a picture.**
 * digitopia.co sends the reader to a *2024* report; the instruction with the captures
 * was *"en aşağıda önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a
 * yönlendirelim"*. So the CTA, the cover and the sentence all name the Energy Industry
 * Focus cut of the Digital & AI Maturity Report 2026, which is the report this site
 * actually publishes.
 *
 * That is settled, so it is not what `check` asks about: the same swap was left open
 * on the Automotive page and has now been answered ten times.
 */
export const OUTLOOK_EN_MATURITY = {
  heading: 'How Does Your Digital Maturity Compare?',
  body: 'The Energy Industry Focus cut of the Digital & AI Maturity Report 2026 reveals where companies stand today, and where they need to go. Compare your performance with leaders, average performers, and laggards. Are you leading the shift or reacting to it? Use our data to compare your performance, assess your risks, and find the fastest route to transformation.',
  ctaLabel: 'Explore the Energy Industry Focus report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_EN_SLUG}`,
  /** That report's cover, which this site already holds. */
  photo: {
    src: '/photo/daimi-report-2026-energy-cover.webp',
    alt: 'Digital & AI Maturity Report 2026, Energy Focus',
  } satisfies Photo,
  check:
    'The destination here is decided, not transcribed: this band points at the 2026 report rather than the “2024 Energy & Utilities Digital Maturity Report” the live page offers, which is what you asked for — so the CTA label and the sentence naming the report are the 2026 cut’s. Four things left to decide. This outlook covers Energy **and** Utilities, and the 2026 study publishes those as two separate Industry Focus cuts; this band points at the Energy one, so say if it should point at Utilities instead, or at both. The rest of the paragraph is the live one, so it wants a reading. The heading is the one every outlook page here uses; the live one runs two headings together — “How Does Your Digital Maturity Compare to Industry Leaders? Benchmark Your Digital Maturity” — which reads as a template left half-filled, so say if the longer form is really wanted. And the cover beside it is not on the live page at all: it is here because the other nine Future 2030 outlook pages show the maturity cut they point at, and it should come out if this band is meant to stay text-only.',
}

/**
 * The closing band, which is where the live page's gated form sits.
 *
 * Its heading is the live band's own — "Start Building Your 2030 Advantage", the same
 * close the Retail and Insurance pages have rather than the Logistics page's plain
 * "Download the Full Report".
 *
 * **Its sentence is the live band's with one word corrected.** digitopia.co offers
 * "the full Future 2030 **Insurance** Outlook" here, on the Energy page. That is a
 * template left half-filled, the same slip that put a Retail maturity CTA on the
 * Pharmaceutical and Banking pages, so it says Energy & Utilities here and `check`
 * records the change.
 */
export const OUTLOOK_EN_CLOSE = {
  heading: 'Start Building Your 2030 Advantage',
  body: 'Get the full Future 2030 Energy & Utilities Outlook with maturity forecasts, strategic priorities, and practical next steps to move faster with confidence.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  check:
    'Two things, and the first is a correction rather than a question: the live page’s closing sentence offers “the full Future 2030 **Insurance** Outlook” on the Energy page, which is a template left half-filled, so it reads “Energy & Utilities Outlook” here. Say if that is wrong. The second is the gating. On digitopia.co this band is a HubSpot form — first and last name, business email, company, job title, phone, country, industry, a marketing opt-in and the privacy statement — and the PDF arrives afterwards. These report pages are prerendered with no JavaScript, so the form cannot be embedded here, and every Download button on this page links straight to the PDF in our own bucket, which is what the other thirty reports on this site do. Say if this report should be gated behind the form instead.',
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `outlookLogisticsChecks()` uses, and appended to the same
 * list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file. This page arrived
 * with all six of its images. What is left is transcription nobody has read against
 * the original, one destination this site cannot offer, one sector slip corrected, one
 * image this page shows that the reference does not, and the gating decision.
 */
export function outlookEnergyChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: OUTLOOK_EN_INTRO.check },
    { section: 'outlook', check: OUTLOOK_EN_FIGURE.check },
    { section: 'requirements', check: OUTLOOK_EN_REQUIREMENTS.check },
    { section: 'predictions', check: OUTLOOK_EN_PREDICTIONS.check },
    { section: 'maturity', check: OUTLOOK_EN_MATURITY.check },
    { section: 'download', check: OUTLOOK_EN_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${OUTLOOK_EN_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
